import React from 'react'
import { cloneDeep } from 'lodash'

import Preloader from 'components/Preloader/Preloader'
import Toast, { TOAST_TYPE } from 'components/Toast/Toast'
import Button from 'components/Forms/Button/Button'
import SEO from 'components/SEO/SEO'

import PageTopbar from 'layouts/Dashboard/components/PageTopbar/PageTopbar'

import TemplatesTable from './components/TemplatesTable/TemplatesTable'
import EditTemplateModal from './components/EditTemplateModal/EditTemplateModal'
import CreateTemplateModal from './components/CreateTemplateModal/CreateTemplateModal'
import RemoveTemplateModal from './components/RemoveTemplateModal/RemoveTemplateModal'

import { getRefreshedParams, getTableParams, setTableParams } from 'utils/table'
import { getTranslation } from 'utils/translations'
import { asyncSetState } from 'utils/react'

import API from 'api'

import { TABLE_PARAMS, MODALS } from './constants'

class BlocksTemplatesPage extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            isReady: false,

            templates: {
                isLoading: false,
                lastResponse: null,
                requestParams: getTableParams(this.props, TABLE_PARAMS),
                list: [],
            },

            modal: {
                isOpen: false,
                name: '',
                payload: null,
            },
        }
        this.baseState = this.state
    }

    async componentDidMount() {
        await Promise.all([this.getTemplates()])
        this.setState({
            isReady: true,
        })
    }

    getTemplates = async (isClear = true) => {
        try {
            const {
                templates: { requestParams },
            } = this.state
            this.setState(prevState => ({
                templates: {
                    ...prevState.templates,
                    isLoading: true,
                },
            }))

            setTableParams(this.props, requestParams)

            const response = await API.BLOCKS_TEMPLATES.GET_TEMPLATES(requestParams.blockTypeId, requestParams)

            await asyncSetState(this, prevState => ({
                templates: {
                    ...prevState.templates,
                    lastResponse: response,
                    list: isClear ? response.content : [...prevState.templates.list, ...response.content],
                },
            }))
        } catch (err) {
            console.error(err)
            Toast(TOAST_TYPE.ERROR)
        } finally {
            this.setState(prevState => ({
                templates: {
                    ...prevState.templates,
                    isLoading: false,
                },
            }))
        }
    }

    openModal = async (name, payload = null) => {
        const additionalPayload = {}
        switch (name) {
            case MODALS.EDIT_TEMPLATE: {
                additionalPayload.translations = {
                    name: null,
                    description: null,
                }
                const { nameTranslationId, descriptionTranslationId } = payload.template
                additionalPayload.translations.name = await getTranslation(nameTranslationId)
                if (descriptionTranslationId) {
                    additionalPayload.translations.description = await getTranslation(descriptionTranslationId)
                }
                break
            }
            default:
                break
        }

        this.setState(prevState => ({
            modal: {
                ...prevState.modal,
                isOpen: true,
                name,
                payload: {
                    ...payload,
                    ...additionalPayload,
                },
            },
        }))
    }
    closeModal = async () => {
        this.setState({
            modal: {
                ...this.baseState.modal,
                isOpen: false,
            },
        })
    }

    createTemplate = async data => {
        await API.BLOCKS_TEMPLATES.CREATE_TEMPLATE(data)
        await this.getTemplates()
    }
    updateTemplate = async data => {
        const _data = cloneDeep(data)

        const { nameTranslationId, descriptionTranslationId } = _data
        if (nameTranslationId && !data.name) {
            _data.name = (await getTranslation(nameTranslationId)).translations
            delete _data.nameTranslationId
        }
        if (descriptionTranslationId && !_data.description) {
            _data.description = (await getTranslation(nameTranslationId)).translations
            delete _data.descriptionTranslationId
        }
        await API.BLOCKS_TEMPLATES.UPDATE_TEMPLATE(_data)
        await this.getTemplates()
    }
    removeTemplate = async id => {
        await API.BLOCKS_TEMPLATES.REMOVE_TEMPLATE(id)
        await this.getTemplates()
    }

    changeRequestParamsAndRefresh = async (data = [], isClear = true) => {
        const {
            templates: { requestParams },
        } = this.state

        if (data.find(item => item.field !== 'page')) {
            data.push({
                field: 'page',
                value: 0,
            })
        }

        await asyncSetState(this, prevState => ({
            templates: {
                ...prevState.templates,
                requestParams: getRefreshedParams(requestParams, data),
            },
        }))

        await this.getTemplates(isClear)
    }

    render() {
        const { isReady, templates, modal } = this.state

        const isOpenedModal = name => modal.isOpen && modal.name === name

        return (
            <>
                <SEO title="Blocks templates" />

                {isReady ? (
                    <>
                        <PageTopbar title="Blocks templates">
                            <Button
                                content="+ Create template"
                                variant="primary"
                                onClick={() => this.openModal(MODALS.CREATE_TEMPLATE)}
                            />
                        </PageTopbar>
                        <TemplatesTable
                            list={templates.list}
                            methods={{
                                updateTemplate: this.updateTemplate,
                                openModal: this.openModal,
                                changeRequestParamsAndRefresh: this.changeRequestParamsAndRefresh,
                            }}
                            pagination={{
                                currentPage: templates.lastResponse.number,
                                totalPages: templates.lastResponse.totalPages,
                                perPageElements: templates.lastResponse.numberOfElements,
                                totalElements: templates.lastResponse.totalElements,
                            }}
                            requestParams={templates.requestParams}
                            onExpand={() =>
                                this.changeRequestParamsAndRefresh(
                                    [
                                        {
                                            field: 'page',
                                            value: templates.lastResponse.number + 1,
                                        },
                                    ],
                                    false,
                                )
                            }
                            onChangePage={page =>
                                this.changeRequestParamsAndRefresh([
                                    {
                                        field: 'page',
                                        value: page,
                                    },
                                ])
                            }
                            isLoading={templates.isLoading}
                        />

                        {isOpenedModal(MODALS.EDIT_TEMPLATE) && (
                            <EditTemplateModal
                                data={modal.payload}
                                onSubmit={this.updateTemplate}
                                onClose={this.closeModal}
                            />
                        )}
                        {isOpenedModal(MODALS.CREATE_TEMPLATE) && (
                            <CreateTemplateModal onSubmit={this.createTemplate} onClose={this.closeModal} />
                        )}
                        {isOpenedModal(MODALS.REMOVE_TEMPLATE) && (
                            <RemoveTemplateModal
                                data={modal.payload}
                                onSubmit={this.removeTemplate}
                                onClose={this.closeModal}
                            />
                        )}
                    </>
                ) : (
                    <Preloader />
                )}
            </>
        )
    }
}

export default BlocksTemplatesPage
